import { useEffect, useState } from 'react';
import apiCoreAdapter from '../hooks/apiCoreAdapter';
import psiPlatformAdapter from '../hooks/psiPlatformAdapter';
import { getPsychologistMetrics } from '../hooks/api/psychologist/metrics';
import { usePsychologistContext } from '../contexts/PsychologistContext';
import { NEW_STATUS_ID } from '../utils/psychologistStatus';

/**
 * ¿La psicóloga está restringida a la Agenda? Misma regla que `AppShell` en la web: una NZ recién creada (status "new")
 * se queda solo en la Agenda hasta que carga horarios y tiene (o tuvo) algún proceso. La app mobile NO puede editar
 * horarios, así que en vez de dejarla sin hacer nada se le muestra un aviso para completarlo desde la plataforma web y
 * se le ocultan Pacientes y Perfil.
 *
 * Falla ABIERTO: si no se pudo averiguar (sin red, error), no se restringe.
 */
export interface PwaRestriction {
  loading: boolean;
  restricted: boolean;
}

export function usePwaRestriction(): PwaRestriction {
  const { psychologist } = usePsychologistContext();
  const id = psychologist?.id;
  const [state, setState] = useState<PwaRestriction>({ loading: true, restricted: false });

  useEffect(() => {
    if (!id) {
      setState({ loading: false, restricted: false });
      return;
    }
    let cancelled = false;
    const done = (restricted: boolean) => {
      if (!cancelled) setState({ loading: false, restricted });
    };
    setState((s) => ({ ...s, loading: true }));

    (async () => {
      try {
        const profile = await apiCoreAdapter.psychologistProfile();
        if (profile.ranking_category !== 'NZ' || profile.status_id !== NEW_STATUS_ID) return done(false);
        const [schedules, metrics] = await Promise.all([
          psiPlatformAdapter.psychologistWorkSchedules({ active: true, per_page: 1 }).catch(() => null),
          getPsychologistMetrics().catch(() => null),
        ]);
        const hasWorkSchedules = (schedules?.results.length ?? 0) > 0;
        const everHadProcesses = (metrics?.active_processes ?? 0) + (metrics?.inactive_processes ?? 0) > 0;
        // Sin poder leer horarios ni métricas no se la deja trabada: solo se restringe con datos que lo confirmen.
        if (schedules === null || metrics === null) return done(false);
        done(!(hasWorkSchedules && everHadProcesses));
      } catch {
        done(false);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [id]);

  return state;
}
